import type { NavigationDestinationId } from '../../../components/Layout/navigationGroups';
import type { ConsultationRole, OnboardingStep } from '../types';

type Params = {
  spaceName: string;
  role: ConsultationRole;
  /** Below `md` the navigation sits in a drawer and the search behind an icon. */
  isPhone: boolean;
};

const WELCOME_INTRO: Record<ConsultationRole, string> = {
  board_member:
    'En tant que membre du conseil syndical, vous suivez ici le travail de gestion de la copropriété : sujets, devis, réunions et documents.',
  reader:
    'Retrouvez ici la vie de la résidence : les dossiers en cours, les dates à venir, les documents et les prestataires.',
};

const MEETINGS_DESCRIPTION: Record<ConsultationRole, string> = {
  board_member: 'Les réunions du conseil syndical, leur ordre du jour et leurs comptes rendus.',
  reader: 'Les réunions du conseil syndical et leurs comptes rendus.',
};

// The inbox is for admins only: neither readers nor council members have it in their menu.
const FOLLOW_UP_IDS: NavigationDestinationId[] = ['home'];
const DATES_IDS: NavigationDestinationId[] = ['calendar', 'meetings'];
const REFERENCE_IDS: NavigationDestinationId[] = ['topics', 'providers', 'documents'];

export function getOnboardingSteps(params: Params): OnboardingStep[] {
  const searchHint = params.isPhone
    ? 'Touchez la loupe en haut de l\'écran pour chercher, ou parcourez le référentiel.'
    // Non-breaking spaces inside the guillemets so they never wrap away from the word.
    : 'Utilisez «\u00A0Rechercher\u00A0» en haut du menu, ou parcourez le référentiel.';

  return [
    {
      id: 'welcome',
      title: `Bienvenue dans ${params.spaceName}`,
      paragraphs: [
        WELCOME_INTRO[params.role],
        'Votre accès est en consultation : vous voyez tout, et les gestionnaires de l\'espace le tiennent à jour.',
      ],
      highlightedIds: [],
    },
    {
      id: 'follow-up',
      title: 'Suivez ce qui avance',
      paragraphs: ['Chaque sujet est un dossier de la copropriété : son état et l\'historique de ce qui a été fait.'],
      points: [
        { label: 'Sujets en cours', description: 'Les dossiers ouverts, avec une vue synthèse pour l\'essentiel.' },
      ],
      highlightedIds: FOLLOW_UP_IDS,
    },
    {
      id: 'dates',
      title: 'Ne manquez aucune date',
      paragraphs: ['Interventions, visites, réunions : les rendez-vous de la résidence, annoncés à l\'avance.'],
      points: [
        { label: 'Calendrier', description: 'Les passages des prestataires, les rendez-vous et les échéances.' },
        { label: 'Réunions', description: MEETINGS_DESCRIPTION[params.role] },
      ],
      highlightedIds: DATES_IDS,
    },
    {
      id: 'reference',
      title: 'Retrouvez une information',
      paragraphs: [`Un contrat, un numéro, un ancien dossier ? ${searchHint}`],
      points: [
        { label: 'Documents', description: 'Contrats, factures, devis et comptes rendus.' },
        { label: 'Prestataires', description: 'Les entreprises qui interviennent, et comment les joindre.' },
        { label: 'Tous les sujets', description: 'Tous les dossiers, y compris ceux qui sont clos.' },
      ],
      highlightedIds: REFERENCE_IDS,
    },
  ];
}
